import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Bitcoin,
  BookOpen,
  Clapperboard,
  FileText,
  Newspaper,
  Package,
  Pin,
  TrendingUp,
  Users,
  Clock,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { useChannelStore } from "@/stores/channelStore";
import { useLanguageStore } from "@/stores/languageStore";
import { cn } from "@/lib/cn";
import { chooseLocalizedText } from "@/lib/localizedText";
import { PostSchedulerSection } from "@/pages/Channels/PostSchedulerSection";
import { ContentTemplatesSection } from "@/pages/Channels/ContentTemplatesSection";
import { AnalyticsSection } from "@/pages/Channels/AnalyticsSection";
import type { ChannelType } from "@/stores/channelStore";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const PAGE_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { ...SPRING, staggerChildren: 0.05 },
  },
};

const SECTION_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: SPRING },
};

interface TypeOption {
  value: ChannelType;
  label: string;
  description: string;
  icon: LucideIcon;
  tone: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  { value: "crypto", label: "Crypto", description: "Coins, prices, trading updates", icon: Bitcoin, tone: "bg-amber-50 text-amber-600" },
  { value: "ecommerce", label: "E-Commerce", description: "Products, offers, order updates", icon: Package, tone: "bg-emerald-50 text-emerald-600" },
  { value: "news", label: "News", description: "Daily posts and announcements", icon: Newspaper, tone: "bg-blue-50 text-blue-600" },
  { value: "education", label: "Education", description: "Courses, tips, learning content", icon: BookOpen, tone: "bg-violet-50 text-violet-600" },
  { value: "entertainment", label: "Entertainment", description: "Media, shows, community content", icon: Clapperboard, tone: "bg-rose-50 text-rose-600" },
  { value: "community", label: "Community", description: "Groups, members, discussions", icon: Users, tone: "bg-cyan-50 text-cyan-600" },
  { value: "other", label: "Other", description: "Anything that does not fit above", icon: Pin, tone: "bg-slate-50 text-slate-600" },
];

function navigateBack() {
  window.location.hash = "/channels";
}

interface ChannelDetailPageProps {
  channelId: string;
}

export function ChannelDetailPage({ channelId }: ChannelDetailPageProps) {
  const language = useLanguageStore((state) => state.language);
  const [hasLoadedChannels, setHasLoadedChannels] = useState(false);
  const {
    selectedChannel,
    scheduledPosts,
    isLoading,
    selectChannel,
    fetchChannels,
    updateChannelType,
    addScheduledPost,
    cancelScheduledPost,
  } = useChannelStore();

  useEffect(() => {
    setHasLoadedChannels(false);
    fetchChannels().finally(() => {
      selectChannel(channelId);
      setHasLoadedChannels(true);
    });
  }, [channelId, fetchChannels, selectChannel]);

  if (isLoading || (!hasLoadedChannels && !selectedChannel)) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Channel" onBack={navigateBack} />
        <main className="flex-1 px-4 pb-20 pt-2">
          <LoadingSkeleton variant="list" count={4} className="mt-2" />
        </main>
      </div>
    );
  }

  if (!selectedChannel) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Channel not found / ቻናሉ አልተገኘም" onBack={navigateBack} />
        <main className="flex-1 px-4 pb-20 pt-6 text-sm text-[var(--color-text-muted)]">
          {chooseLocalizedText("This channel is unavailable or was removed. / ይህ ቻናል የለም ወይም ተወግዷል።", language)}
        </main>
      </div>
    );
  }

  const channelPosts = scheduledPosts.filter(
    (p) => p.channelId === channelId && p.status === "pending",
  );

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title={selectedChannel.title} onBack={navigateBack} />

      <motion.main
        variants={PAGE_VARIANTS}
        initial="hidden"
        animate="visible"
        className="flex-1 px-4 pb-20 pt-2"
      >
        <OverviewSection
          subscriberCount={selectedChannel.subscriberCount}
          totalPosts={selectedChannel.stats.totalPosts}
          postsThisWeek={selectedChannel.stats.postsThisWeek}
          scheduledCount={selectedChannel.scheduledPosts}
        />

        <ChannelTypeSection
          selectedType={selectedChannel.channelType}
          onSelect={(type) => updateChannelType(channelId, type)}
        />

        <PostSchedulerSection
          channelId={channelId}
          posts={channelPosts}
          onSchedule={addScheduledPost}
          onCancel={cancelScheduledPost}
        />

        <ContentTemplatesSection />

        <AnalyticsSection />
      </motion.main>
    </div>
  );
}

function OverviewSection({
  subscriberCount,
  totalPosts,
  postsThisWeek,
  scheduledCount,
}: {
  subscriberCount: number;
  totalPosts: number;
  postsThisWeek: number;
  scheduledCount: number;
}) {
  const stats = [
    { icon: Users, value: subscriberCount, label: "Subscribers", color: "text-blue-600" },
    { icon: FileText, value: totalPosts, label: "Total Posts", color: "text-emerald-600" },
    { icon: TrendingUp, value: postsThisWeek, label: "This Week", color: "text-amber-600" },
    { icon: Clock, value: scheduledCount, label: "Scheduled", color: "text-violet-600" },
  ];

  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-5">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Overview
      </h2>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.label}
              className={cn(
                "flex flex-col items-center gap-1.5 rounded-[var(--radius-lg)] p-4",
                "bg-[var(--color-surface)] shadow-sm",
              )}
            >
              <Icon className={cn("h-5 w-5", stat.color)} />
              <span className="font-headline text-xl font-bold text-[var(--color-text)]">
                {stat.value.toLocaleString()}
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                {stat.label}
              </span>
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}

function ChannelTypeSection({
  selectedType,
  onSelect,
}: {
  selectedType: ChannelType;
  onSelect: (type: ChannelType) => void;
}) {
  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-5">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Channel Type
      </h2>
      <div className="grid grid-cols-1 gap-2">
        {TYPE_OPTIONS.map((option) => {
          const isSelected = option.value === selectedType;
          const Icon = option.icon;
          return (
            <motion.button
              key={option.value}
              type="button"
              whileTap={{ scale: 0.95 }}
              onClick={() => onSelect(option.value)}
              aria-label={`Set channel type to ${option.label}`}
              aria-pressed={isSelected}
              className={cn(
                "flex items-start gap-3 rounded-2xl border p-3 text-left transition-colors",
                isSelected
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
                  : "border-[var(--color-border)] bg-[var(--color-surface)]",
              )}
            >
              <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", option.tone)}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[var(--color-text)]">{option.label}</span>
                <span className="mt-0.5 block text-xs leading-5 text-[var(--color-text-muted)]">
                  {option.description}
                </span>
              </span>
              {isSelected && <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[var(--color-primary)]" />}
            </motion.button>
          );
        })}
      </div>
    </motion.section>
  );
}
